import pick from 'lodash/pick'
import { flatten } from 'flat'

/**
 * Возвращает карту (ключ - значение) провайдеров для заданного обсервера.
 *
 * Схема работы:
 *      1. В карту добавляются провайдеры, из заданного обсервера (это легко).
 *      2. Рекурсивно обходятся дочерние обсерверы, из них в карту добавляются провайдеры.
 *
 * Пример результата: {
 *      name: Provider<VueComponent>,
 *      "params.0.name": Provider<VueComponent>
 *      "params.0.desc": Provider<VueComponent>
 * }
 *
 * @param {VueComponent} observer Vue компонент, полученный через $ref
 * @return {object} Карта
 */
function makeObserverMap(observer) {
    const providers = []

    function makeMap(observer) {
        const map = {}

        // Добавляем в карту провайдеры из этого обсервера (observer)
        Object.keys(observer.refs).forEach(key => {
            providers.push(observer.refs[key])
            map[key] = providers.length - 1
        })

        // Добавляем в карту провайдеры из дочерних обсерверов (observer.observers)
        observer.observers.forEach(childObserver => {
            const ref = childObserver.$vnode.data.ref

            if (!ref) {
                console.warn(`Observer ${childObserver.vid} must have ref attribute.`)
                return
            }

            const childMap = makeMap(childObserver)

            if (Array.isArray(map[ref])) {
                map[ref].push(childMap)
            } else {
                map[ref] = (map[ref] ? [map[ref], childMap] : childMap)
            }
        })

        return map
    }

    const map = flatten(makeMap(observer))
    Object.keys(map).forEach(key => { map[key] = providers[map[key]] })

    return map
}

/**
 * Подмешивает в компонент ValidationObserver ошибки с бекенда.
 *
 * @param {VueComponent} observer
 * @param {object} errors
 */
export function mixinApiErrors(observer, errors) {
    if (!observer || !errors) {
        return
    }

    if ((observer.$vnode.componentOptions.tag || null) !== 'ValidationObserver') {
        console.warn(`mixinApiErrors calls on ValidationObserver only.`)
        return
    }

    const observerMap = makeObserverMap(observer)

    Object.keys(errors.errors).forEach(key => {
        observerMap[key] && observerMap[key].applyResult({
            errors: [errors.errors[key]],
            valid: false,
            failedRules: {
                'api': errors.errors[key]
            }
        })
    })
}

/**
 * Директива для отображением ошибок валидации в компоненте ValidationProvider.
 *
 * В компоненте будет создан контейнер "div.invalid-feedback".
 * В нем отображается сообщение ошибки (через запятую, если их несколько).
 *
 * Схема работы:
 * - Если failedRules не пустой, навешивает класс is-invalid.
 * - Если failedRules не пустой, отображает сообщение ошибки в контейнере "div.invalid-feedback"
 *
 * Для фильтрации отображаемых сообщений можно использовать модификаторы:
 * v-errors.required        - отображается только сообщение из правила валидации required.
 * v-errors.required.max    - отображается только сообщение из правил валидации required и/или max.
 *
 * На раскрашивание (добавляемый класс is-invalid) модификаторы не влияют!
 */
export const vErrors = {
    bind(el, binding, vnode) {
        if (vnode.componentOptions === undefined || vnode.componentOptions.tag !== 'ValidationProvider') {
            return
        }

        // Контейнер для отображения сообщений
        const div = document.createElement('div')
        div.className = 'invalid-feedback'
        el.appendChild(div)

        // Фильтр отображаемых сообщений
        const only = Object.keys(binding.modifiers)

        vnode.componentInstance.$watch('failedRules', function (failedRules) {
            el.classList.toggle('is-invalid', Object.keys(failedRules).length !== 0)
            div.innerText = Object.values(pick(failedRules, only)).join(', ')
        }, {
            deep: true,
            immediate: true
        })
    }
}
